import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Brain, Target, Zap, Shield, Globe, Star, CheckCircle, Users, BarChart3, Settings, Home, Briefcase, BookOpen } from 'lucide-react'
import { Testimonials } from '../components/Testimonials'
import { QuickStartGuide } from '../components/QuickStartGuide'

// Tools array
const tools = [
  {
    name: 'General Picker Wheel',
    path: '/tools/picker',
    desc: 'A general random picker wheel for any custom options.',
    icon: '🎡',
    category: 'general',
    popular: true
  },
  {
    name: 'Name Picker Wheel',
    path: '/tools/name-picker',
    desc: 'Randomly pick a name from a list of names.',
    icon: '👤',
    category: 'people'
  },
  {
    name: 'Team Picker Wheel',
    path: '/tools/team-picker',
    desc: 'Randomly group names into teams.',
    icon: '👥',
    category: 'people'
  },
  {
    name: 'Yes No Picker Wheel',
    path: '/tools/yesno-picker',
    desc: 'Randomly pick Yes or No.',
    icon: '✅',
    category: 'simple'
  },
  {
    name: 'Number Picker Wheel',
    path: '/tools/number-picker',
    desc: 'Randomly pick a number from a range or list.',
    icon: '🔢',
    category: 'simple'
  },
  {
    name: 'Letter Picker Wheel',
    path: '/tools/letter-picker',
    desc: 'Randomly pick a letter from A-Z.',
    icon: '🔤',
    category: 'simple'
  },
  {
    name: 'Country Picker Wheel',
    path: '/tools/country-picker',
    desc: 'Randomly pick a country.',
    icon: '🌍',
    category: 'geography'
  },
  {
    name: 'State Picker Wheel',
    path: '/tools/state-picker',
    desc: 'Randomly pick a US state.',
    icon: '🗽',
    category: 'geography'
  },
  {
    name: 'Color Picker Wheel',
    path: '/tools/color-picker',
    desc: 'Randomly pick a color.',
    icon: '🎨',
    category: 'creative'
  },
  {
    name: 'Date Picker Wheel',
    path: '/tools/date-picker',
    desc: 'Randomly pick a date.',
    icon: '📅',
    category: 'time'
  },
  {
    name: 'Instagram Comment Picker Wheel',
    path: '/tools/instagram-comment-picker',
    desc: 'Randomly pick a winner from Instagram comments.',
    icon: '💬',
    category: 'social'
  },
  {
    name: 'MLB Picker Wheel',
    path: '/tools/mlb-picker',
    desc: 'Randomly pick a MLB team.',
    icon: '⚾',
    category: 'sports'
  },
  {
    name: 'NBA Picker Wheel',
    path: '/tools/nba-picker',
    desc: 'Randomly pick a NBA team.',
    icon: '🏀',
    category: 'sports'
  },
  {
    name: 'NFL Picker Wheel',
    path: '/tools/nfl-picker',
    desc: 'Randomly pick a NFL team.',
    icon: '🏈',
    category: 'sports'
  }
]

const analysisTools = [
  {
    name: 'Pros & Cons Analysis',
    path: '/decision-analysis/pros-cons',
    desc: 'Analyze pros, cons, risks and opportunities with AI',
    icon: '⚖️',
    features: ['Detailed pros/cons', 'Risk assessment', 'Next steps', 'Confidence scoring'],
    popular: true
  },
  {
    name: 'Causal Chain Analysis',
    path: '/decision-analysis/causal-chain',
    desc: 'Show decision outcomes in causal chains',
    icon: '🔗',
    features: ['Causal mapping', 'Impact analysis', 'Long-term effects', 'Risk assessment']
  },
  {
    name: 'Context Analysis',
    path: '/decision-analysis/context',
    desc: 'Get personalized advice based on your context',
    icon: '🎯',
    features: ['Personalized insights', 'Context-aware advice', 'Situational analysis', 'Custom recommendations']
  },
  {
    name: 'Multi-Option Analysis',
    path: '/decision-analysis/multi-option',
    desc: 'Compare multiple options systematically',
    icon: '📊',
    features: ['Systematic comparison', 'Scoring matrix', 'Weighted analysis', 'Option ranking']
  }
]



export const HomePage: React.FC = () => {
  const navigate = useNavigate()

  // Handle tool click
  const handleToolClick = (path: string) => {
    navigate(path)
  }

  return (
    <>
      <Helmet>
        <title>AI Decision Maker - Smart Decision Assistant with AI Analysis</title>
        <meta name="description" content="Make better decisions with our AI decision maker. Features a spinning wheel for random selection and AI-powered analysis to help you choose wisely." />
        <meta name="keywords" content="ai decision maker, decision maker, ai decision assistant, decision wheel, ai analysis, smart decisions, decision tool, ai powered decisions, free decision maker, online decision tool, decision making app, ai decision analysis, decision helper, choice maker, decision support" />
        <meta property="og:title" content="AI Decision Maker - Smart Decision Assistant with AI Analysis" />
        <meta property="og:description" content="Make better decisions with our AI decision maker. Features a spinning wheel for random selection and AI-powered analysis to help you choose wisely." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aidecision.org" />
        <meta property="og:image" content="https://aidecision.org/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:title" content="AI Decision Maker - Smart Decision Assistant with AI Analysis" />
        <meta name="twitter:description" content="Make better decisions with our AI decision maker. Features a spinning wheel for random selection and AI-powered analysis to help you choose wisely." />
        <meta name="twitter:image" content="https://aidecision.org/twitter-image.png" />
        <link rel="canonical" href="https://aidecision.org" />
        
        {/* Enhanced Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "AI Decision Maker",
            "alternateName": "AI Decision Assistant",
            "description": "Free online AI decision maker with AI-powered analysis and spinning wheel tool. Make smarter choices with instant AI insights and 14+ specialized decision tools.",
            "url": "https://aidecision.org",
            "applicationCategory": "ProductivityApplication",
            "operatingSystem": "Web Browser",
            "browserRequirements": "Requires JavaScript. Requires HTML5.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "featureList": [
              "AI-powered decision analysis",
              "Spinning wheel for random selection",
              "Multiple decision modes",
              "Instant AI insights",
              "Free online tool",
              "14+ specialized decision wheels",
              "Pros & Cons analysis",
              "Causal chain analysis",
              "Context analysis",
              "Multi-option analysis"
            ],
            "author": {
              "@type": "Organization",
              "name": "AI Decision Maker",
              "url": "https://aidecision.org"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "1250",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Sarah Johnson"
                },
                "reviewBody": "Amazing AI decision maker! The AI analysis really helps me make better choices."
              }
            ]
          })}
        </script>
        
        {/* Additional Structured Data for Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "AI Decision Maker",
            "url": "https://aidecision.org",
            "logo": "https://aidecision.org/logo.png",
            "description": "Free online AI decision maker and decision assistant with AI-powered analysis",
            "sameAs": [
              "https://aidecision.org"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "url": "https://aidecision.org/contact"
            }
          })}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <h1 className="text-5xl font-extrabold text-primary-700 mb-6">
            Make Better Decisions with AI
          </h1>
          <p className="text-xl text-gray-700 mb-6 font-semibold max-w-3xl mx-auto">
            Get instant AI analysis for any decision. No more guessing, no more regrets.
          </p>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-8">
            The smartest decision tool that combines AI analysis with instant results.
          </p>
          
          {/* Hero CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button 
              onClick={() => navigate('/tools/yesno-picker')}
              className="group bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center space-x-2"
            >
              <span>Start Decision Wheel</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <a 
              href="/decision-analysis/pros-cons" 
              className="group bg-white hover:bg-gray-50 text-primary-600 font-semibold py-3 px-8 rounded-lg border-2 border-primary-600 transition-all duration-300 hover:scale-105 flex items-center space-x-2"
            >
              <span>Start Decision Analysis</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </section>

        {/* Quick Benefits */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl mb-2">⚡</div>
                <div className="font-semibold text-gray-900">3-Second Analysis</div>
                <div className="text-sm text-gray-600">Instant AI insights</div>
              </div>
              <div>
                <div className="text-2xl mb-2">🎯</div>
                <div className="font-semibold text-gray-900">Smart Recommendations</div>
                <div className="text-sm text-gray-600">Data-driven advice</div>
              </div>
              <div>
                <div className="text-2xl mb-2">🔒</div>
                <div className="font-semibold text-gray-900">100% Free</div>
                <div className="text-sm text-gray-600">No hidden costs</div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Start Guide */}
        <QuickStartGuide />

        {/* AI Analysis Tools Section */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 
              className="text-3xl font-bold text-gray-900 mb-4 cursor-pointer hover:text-blue-600 transition-colors"
              onClick={() => navigate('/analysis')}
            >
              AI-Powered Decision Analysis
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get intelligent insights and recommendations for your decisions with our advanced AI analysis tools.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {analysisTools.map(tool => (
              <div key={tool.path} className="bg-white rounded-xl shadow-lg p-6 border hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105" onClick={() => navigate(tool.path)}>
                <div className="text-4xl mb-4">{tool.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{tool.name}</h3>
                <p className="text-gray-600 mb-4">{tool.desc}</p>
                <div className="space-y-2">
                  {tool.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-500">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      {feature}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center text-primary-600 font-medium">
                  <span>Try Now</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Decision Wheels Section */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 
              className="text-3xl font-bold text-gray-900 mb-4 cursor-pointer hover:text-blue-600 transition-colors"
              onClick={() => navigate('/wheel')}
            >
              Specialized Decision Wheels
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose from our collection of specialized wheels designed for specific decision-making scenarios.
            </p>
          </div>
          
          {/* All Tools - Simplified */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {tools.map(tool => (
              <div key={tool.path} className="bg-white rounded-xl shadow-lg p-4 border hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105" onClick={() => handleToolClick(tool.path)}>
                <div className="text-3xl mb-2">{tool.icon}</div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1">{tool.name}</h4>
                <p className="text-xs text-gray-600 mb-3">{tool.desc}</p>
                <div className="flex items-center text-primary-600 font-medium text-xs">
                  <span>Try Now</span>
                  <ArrowRight className="w-3 h-3 ml-1" />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features Section */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose AI Decision Maker?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our comprehensive platform offers everything you need to make better decisions with confidence.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Brain className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">AI-Powered Insights</h3>
              <p className="text-gray-600">Get intelligent analysis, pros/cons, risk assessment, and personalized recommendations for every decision.</p>
            </div>
            
            <div className="text-center group">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Target className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Multiple Decision Modes</h3>
              <p className="text-gray-600">Choose from normal, elimination, or accumulation modes to match your decision-making style and needs.</p>
            </div>
            
            <div className="text-center group">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Zap className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Instant Results</h3>
              <p className="text-gray-600">Get immediate decisions and AI analysis in seconds. No waiting, no complicated setup required.</p>
            </div>
            
            <div className="text-center group">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Privacy & Security</h3>
              <p className="text-gray-600">Your data stays private. No personal information is collected or shared. Local storage for your decisions.</p>
            </div>
            
            <div className="text-center group">
              <div className="bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Globe className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Free & Accessible</h3>
              <p className="text-gray-600">Completely free to use with no hidden costs. Works on any device with an internet connection.</p>
            </div>
            
            <div className="text-center group">
              <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Star className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Professional Templates</h3>
              <p className="text-gray-600">Use pre-built templates for common decisions like career changes, home purchases, and more.</p>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <Testimonials />

        {/* Use Cases Section */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Perfect For Every Decision</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From daily choices to life-changing decisions, AI Decision Maker helps you make better choices.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 border">
              <div className="flex items-center mb-4">
                <Briefcase className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Career Decisions</h3>
              </div>
              <p className="text-gray-600 mb-4">Job changes, career paths, skill development, and professional growth decisions.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Should I change jobs?</li>
                <li>• Which skill to learn next?</li>
                <li>• Career path evaluation</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 border">
              <div className="flex items-center mb-4">
                <Home className="w-8 h-8 text-green-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Life Choices</h3>
              </div>
              <p className="text-gray-600 mb-4">Personal decisions about relationships, lifestyle, and major life changes.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Should I move to a new city?</li>
                <li>• Buy vs rent decision</li>
                <li>• Relationship choices</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 border">
              <div className="flex items-center mb-4">
                <Users className="w-8 h-8 text-purple-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Team Decisions</h3>
              </div>
              <p className="text-gray-600 mb-4">Group decisions, team activities, and collaborative choice-making.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Team member selection</li>
                <li>• Project prioritization</li>
                <li>• Group activities</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 border">
              <div className="flex items-center mb-4">
                <BookOpen className="w-8 h-8 text-orange-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Educational Choices</h3>
              </div>
              <p className="text-gray-600 mb-4">Academic decisions, course selection, and learning path choices.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Course selection</li>
                <li>• Study topics</li>
                <li>• Learning priorities</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 border">
              <div className="flex items-center mb-4">
                <BarChart3 className="w-8 h-8 text-red-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Business Decisions</h3>
              </div>
              <p className="text-gray-600 mb-4">Strategic business choices, investments, and organizational decisions.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Investment choices</li>
                <li>• Strategy selection</li>
                <li>• Resource allocation</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 border">
              <div className="flex items-center mb-4">
                <Settings className="w-8 h-8 text-gray-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Daily Choices</h3>
              </div>
              <p className="text-gray-600 mb-4">Everyday decisions that make life easier and more organized.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• What to eat today?</li>
                <li>• Weekend activities</li>
                <li>• Shopping choices</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Why Choose AI Decision Maker?</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-700 mb-2">4</div>
                <div className="text-gray-700 font-medium">AI Analysis Types</div>
                <div className="text-sm text-gray-500 mt-1">Pros/cons, causal chains, context, multi-option</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-700 mb-2">14</div>
                <div className="text-gray-700 font-medium">Decision Wheels</div>
                <div className="text-sm text-gray-500 mt-1">Specialized tools for every scenario</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-700 mb-2">100%</div>
                <div className="text-gray-700 font-medium">Free to Use</div>
                <div className="text-sm text-gray-500 mt-1">No hidden costs or subscriptions</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-700 mb-2">24/7</div>
                <div className="text-gray-700 font-medium">AI Available</div>
                <div className="text-sm text-gray-500 mt-1">Get help anytime, anywhere</div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Make Better Decisions?</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Join thousands of users who are already making smarter choices with AI Decision Maker.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => navigate('/tools/yesno-picker')}
                className="group bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center space-x-2"
              >
                <span>Start Decision Wheel</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a 
                href="/decision-analysis/pros-cons" 
                className="group bg-white hover:bg-gray-50 text-primary-600 font-semibold py-3 px-8 rounded-lg border-2 border-primary-600 transition-all duration-300 hover:scale-105 flex items-center space-x-2"
              >
                <span>Start Decision Analysis</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  )
} 